import React, { useState, useEffect } from 'react';
import "../components/styles/CustomProductSecondPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useLoggedInUserId } from "../api/GetLoggedInUserId";
import { toast } from "../utils/toast";

const CustomProductDetail = () => {
    let navigate = useNavigate();
    const { applicationArticleId } = useParams();
    const [files, setFiles] = useState([]); // 파일 업로드 전 파일명
    const [uploadedFiles, setUploadedFiles] = useState([]); // 파일 업로드 후 저장된 파일명
    const [formData, setFormData] = useState({
        name: '', email: '', phoneNumber: '', title: '', content: '', fileNames: ''
    });
    const [tempArticleId, setTempArticleId] = useState(null);
    const [errors, setErrors] = useState({});
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const MAX_FILE_COUNT = 5;

    const { data: memberId } = useLoggedInUserId();

    const schema = z.object({
        name: z.string().min(2, "이름은 2~10자 이내여야 합니다.").max(10, "이름은 2~10자 이내여야 합니다."),
        email: z.string().email("올바른 이메일 형식이어야 합니다."),
        phoneNumber: z.string().regex(/^010\d{8}$/, "전화번호는 010으로 시작하는 11자리 숫자여야 합니다."),
        title: z.string().min(5, "제목은 5~100자 이내여야 합니다.").max(100, "제목은 5~100자 이내여야 합니다."),
        content: z.string().min(10, "내용은 10~1000자 이내여야 합니다.").max(1000, "내용은 10~1000자 이내여야 합니다."),
    });

    useEffect(() => {
        const fetchApplicationData = async () => {
            try {
                if (memberId) setIsAuthenticated(true);
                const response = await fetch(`/api/application/detail?applicationArticleId=${applicationArticleId}`);
                if (response.ok) {
                    const data = await response.json();
                    setFormData({
                        name: data.name  === "noData" ? "" : data.name,
                        email: data.email === "noData" ? "" : data.email,
                        phoneNumber: data.phoneNumber === "noData" ? "" : data.phoneNumber,
                        title: data.title === "noData" ? "" : data.title,
                        content: data.content === "noData" ? "" : data.content,
                        fileNames: data.fileNames === "noData" ? "" : data.fileNames
                    });
                    setTempArticleId(data.applyCustomProductId);
                    setFiles(data.fileNames ? data.fileNames.split(",") : []);
                }
            } catch (error) {
                console.error("데이터 불러오기 실패:", error);
            }
        };
        fetchApplicationData();
    }, [applicationArticleId, memberId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const validateField = (name, value) => {
        try {
            schema.pick({ [name]: schema.shape[name] }).parse({ [name]: value });
            setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
        } catch (error) {
            if (error instanceof z.ZodError) {
                setErrors((prevErrors) => ({ ...prevErrors, [name]: error.errors[0].message }));
            }
        }
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (files.length + uploadedFiles.length + selectedFiles.length > MAX_FILE_COUNT) {
            toast("파일은 최대 5개까지만 업로드할 수 있습니다.");
            return;
        }
        const validFiles = selectedFiles.filter(file => {
            if (file.size > MAX_FILE_SIZE) {
                toast(`${file.name}은(는) 10MB를 초과하였습니다.`);
                return false;
            }
            return true;
        });
        setFiles(prev => [...prev, ...validFiles]);
    };

    const handleFileUpload = async (e) => {
        const files = e.target.files; // ✅ 파일 가져오기
        if (!files.length) {
            toast("파일을 선택해주세요.");
            return;
        }

        const data = new FormData();
        for (const file of files) {
            data.append("file", file);
        }

        try {
            const response = await fetch("/api/upload-file", {
                method: "POST",
                body: data,
            });

            const result = await response.json();

            if (result.result === "success") {
                setUploadedFiles((prev) => [...prev, result.d]); // storeFileName 저장
                setFiles((prev) => [...prev, result.filename]);
                toast("파일 업로드 성공!");
            } else {
                toast("파일 업로드 실패");
            }
        } catch (error) {
            console.error("파일 업로드 오류:", error);
            toast("서버 오류");
        }
    }

    const handleFileDelete = async (index, type) => {
        if (type === "new") {
            setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
        } else {
            const fileName = uploadedFiles[index];
            try {
                const response = await fetch(`/api/delete-file?fileId=${fileName}`, { method: "DELETE" });
                const result = await response.json();
                if (result.result === "success") {
                    toast("파일 삭제 성공!");
                    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
                } else {
                    toast("파일 삭제 실패");
                }
            } catch (error) {
                console.error("파일 삭제 오류:", error);
                toast("서버 오류");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // const data = new FormData();
        // data.append('name', formData.name);
        // data.append('email', formData.email);
        // data.append('phoneNumber', formData.phoneNumber);
        // data.append('title', formData.title);
        // data.append('content', formData.content);

        // files.forEach((file) => {
        //     data.append('files', file);
        // });

        schema.parse(formData);
        setErrors({});

        // json화
        const data = {
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            title: formData.title,
            content: formData.content,
            fileNames: files.join(",")
        }

        try {
            const response = await fetch('/api/apply/custom-product', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                toast('신청이 성공적으로 제출되었습니다!');
                navigate("/custom-product-first");
            } else {
                toast('신청 제출 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('신청 제출 중 오류:', error);
            toast('서버 오류');
        }
    }

    return (
        <div className="custom-form-page">
            <div className="form-container">
                <h1 className="form-title">맞춤상품 신청 상세 정보</h1>
                <form className="custom-form">
                    {[
                        { label: "이름", name: "name", type: "text" },
                        { label: "이메일", name: "email", type: "email" },
                        { label: "전화번호", name: "phoneNumber", type: "text" },
                        { label: "제목", name: "title", type: "text" },
                    ].map(({ label, name, type }, index) => (
                        <div key={index} className="form-group">
                            <label className="form-label">
                                {label}
                                <span className="required">*</span>
                            </label>
                            <input
                                type={type}
                                name={name}
                                placeholder={`${label}을 입력해 주세요`}
                                className="form-input"
                                value={formData[name]}
                                onChange={handleInputChange}
                                required
                                readOnly={true}
                            />
                            {errors[name] && <p className="error-message">{errors[name]}</p>}
                        </div>
                    ))}
                    <div className="form-group">
                        <label className="form-label">파일<span className="required">*</span></label>
                        {/*<input type="file" className="form-input" onChange={(e) => handleFileUpload(e)}  />*/}
                        <ul className="file-list">
                            {files.map((file, index) => (
                                <li key={index} className="file-item">
                                    {file}
                                    {/*<button type="button" className="delete-button" onClick={() => handleFileDelete(index)}>삭제</button>*/}
                                </li>
                            ))}
                        </ul>
                        <small className="file-info">* 파일당 최대 10MB, 최대 5개까지 업로드 가능합니다.</small>
                    </div>

                    <div className="form-group">
                        <label className="form-label">설명<span className="required">*</span></label>
                        <textarea
                            name="content"
                            placeholder="문의하실 내용을 자유롭게 써주세요(10자이상)"
                            className="form-textarea"
                            value={formData.content}
                            onChange={handleInputChange}
                            required
                            readOnly={true}
                        ></textarea>
                        <span className="char-count">{formData.content.length}/1000</span>
                        {errors.content && <p className="error-message">{errors.content}</p>}
                    </div>

                    <div className="form-warnings">
                        <p className="form-warning">맞춤상품
                            상담 신청시 유의사항</p>
                        <p className="form-warning">- 상담 신청이 완료되면 영업일 1~2일내로 연락을 드리오니, 수신에 유의하여
                            주시기 바랍니다.</p>
                        <p className="form-warning">- 본 상담 신청서는 보다 원활한 상담을 위해 작성하는 초기 단계로, 작성
                            예시를 참고하여 간단하게 작성해주세요. 더 세부적인 사항에 관한 내용은 추후 상담을 통해 전달해주시기 바랍니다.</p>
                        <p className="form-warning">- 임시저장 또는 제출된 상담 신청 내역은 "마이페이지>상담신청내역"에서 볼
                            수 있습니다.</p>
                        <p className="form-warning">- 로그인하지않은 상태로 신청된 상담내역의 다시보기 서비스는 존재하지
                            않습니다. 수정 및 재확인이 필요한 경우 고객센터를 이용해주세요.</p>
                        <p className="form-warning">- 법에 저촉되는 프로그램 문의는 일체 받지 않습니다.</p>
                    </div>
                    {/*<button type="submit" className="submit-button" onClick={handleSubmit}>제출하기</button>*/}
                </form>
            </div>
        </div>
    );
};

export default CustomProductDetail;
